import { create } from 'zustand';
import axios from 'axios';

interface Currency {
  code: string;
  name: string;
  symbol: string;
  iso2: string;
  flag: string;
  allowWalletCreation: boolean;
  supportedNetworks?: [];
  hasSupportingCountries?: boolean;
  dialCode?: string;
  supportingCountries?: string[];
  hasBankList?: boolean;
  paymentMethod: {
    bankTransfer: boolean;
    mobileMoney: boolean;
    stableCurrency?: boolean;
  };
}

interface CurrencyState {
  currencies: Currency[];
  loadingCurrency: boolean;
  hasLoaded: boolean;
  error: string | null;
  loadCurrencies: () => Promise<void>;
  getCurrencyByCode: (code: string) => Currency | undefined;
  getCurrencyByIso2: (iso2: string) => Currency | undefined;
  getFilteredCurrencies: (options?: {
    excludedCodes?: string[];
    includedCodes?: string[];
    onlyBankTransfer?: boolean;
    onlyMobileMoney?: boolean;
    onlyStableCurrency?: boolean; 
  }) => Currency[];
  walletSupportedCurrencies: Currency[];
  hasWalletSupportedLoaded: boolean;
  getWalletSupportedCurrencies: () => Promise<void>;
}

export const useCurrencyStore = create<CurrencyState>((set, get) => ({
  currencies: [],
  loadingCurrency: false,
  hasLoaded: false,
  error: null,
  hasWalletSupportedLoaded: false,
  walletSupportedCurrencies: [],

  loadCurrencies: async () => {
    const { hasLoaded } = get();
    if (hasLoaded) return;

    set({ loadingCurrency: true, error: null });
    try {
       const response = await axios.get(
          `${
            import.meta.env.VITE_BASE_URL
          }/wallet/supported-currencies/all`,
        );

      if (response.data?.success && Array.isArray(response.data.data)) {
        const formattedCurrencies: Currency[] = response.data.data.map(
          (currency: any) => ({
            code: currency.abbr,
            name: currency.name,
            symbol: currency.symbol,
            iso2: currency.iso2,
            flag: currency.flag,
            allowWalletCreation: currency.allowWalletCreation,
            supportedNetworks: currency.supportedNetworks || [], 
            hasBankList: currency.hasBankList ?? false,
            paymentMethod: {
              bankTransfer: currency.paymentMethod?.bankTransfer || false,
              mobileMoney: currency.paymentMethod?.mobileMoney || false,
              stableCurrency: currency.paymentMethod?.stableCurrency || false,
              phoneNumberLength: currency.paymentMethod?.phoneNumberLength || false,
            },
          })
        );
        
        set({ 
          currencies: formattedCurrencies, 
          hasLoaded: true,
          loadingCurrency: false 
        });
      }
    } catch (error) {
      console.error("Failed to load currencies:", error);
      set({ 
        error: "Failed to load currencies", 
        loadingCurrency: false 
      });
    }
  },

  getCurrencyByCode: (code: string) => {
    return get().currencies.find(currency => currency.code === code);
  },

  getCurrencyByIso2: (iso2: string) => {
    return get().currencies.find(currency => currency.iso2 === iso2);
  },

  getFilteredCurrencies: (options = {}) => {
    const { currencies } = get();
    let filtered = [...currencies];

    const {
      excludedCodes = [],
      includedCodes = [],
      onlyBankTransfer = false,
      onlyMobileMoney = false,
      onlyStableCurrency = false
    } = options;

    // Filter by excluded codes
    if (excludedCodes.length > 0) {
      filtered = filtered.filter(currency => 
        !excludedCodes.includes(currency.code)
      );
    }

    // Filter by included codes (whitelist)
    if (includedCodes.length > 0) {
      filtered = filtered.filter(currency => 
        includedCodes.includes(currency.code)
      );
    }

    // Filter by payment method
    if (onlyBankTransfer) {
      filtered = filtered.filter(currency => 
        currency.paymentMethod.bankTransfer
      );
    }

    if (onlyMobileMoney) {
      filtered = filtered.filter(currency => 
        currency.paymentMethod.mobileMoney
      );
    }

    if (onlyStableCurrency) {
      filtered = filtered.filter(currency => 
        currency.paymentMethod.stableCurrency
      );
    }

    return filtered;
  },

  getWalletSupportedCurrencies : async () => {    
    const { hasWalletSupportedLoaded } = get();
    if (hasWalletSupportedLoaded) return;

    set({ loadingCurrency: true, error: null });
    try {
       const response = await axios.get(
          `${
            import.meta.env.VITE_USER_GATEWAY_BASE_URL
          }/wallet/supported-currencies`,
        );

      if (response.data?.success && Array.isArray(response.data.data)) {
        const formattedCurrencies: Currency[] = response.data.data.map(
          (currency: any) => ({
            code: currency.abbr,
            name: currency.name,
            symbol: currency.symbol,
            iso2: currency.iso2,
            flag: currency.flag,
            allowWalletCreation: currency.allowWalletCreation,
            supportedNetworks: currency.supportedNetworks || [], 
            dialCode: currency.dialCode,
            hasSupportingCountries: currency.hasSupportingCountries,
            supportingCountries: currency.supportingCountries,
            paymentMethod: {
              bankTransfer: currency.paymentMethod?.bankTransfer || false,
              mobileMoney: currency.paymentMethod?.mobileMoney || false,
              stableCurrency: currency.paymentMethod?.stableCurrency || false, 
            },
          })
        );
        
        set({ 
          walletSupportedCurrencies: formattedCurrencies,
          hasWalletSupportedLoaded: true,
          loadingCurrency: false 
        });
      }
    } catch (error) {
      console.error("Failed to load currencies:", error);
      set({ 
        error: "Failed to load currencies", 
        loadingCurrency: false 
      });
    }
  }

}));

// Pre-load currencies when store is created
// useCurrencyStore.getState().loadCurrencies();